import { memo, MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import clsx from 'clsx'

import { Box, ClickAwayListener, IconButton, Paper, Popover, makeStyles } from '@material-ui/core'

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import PauseIcon from '@material-ui/icons/Pause'

import { SpacedGroup } from 'UI/Components'

import { SpeakerType } from 'app/types'
import { setUserPreferences } from 'ducks'
import { delay, getTimeString, logToAnalytics } from 'modules'

const enum playMode {
    STOP = 'stop',
    PLAY = 'play'
}

const useStyles = makeStyles({
    name: {
        fontWeight: 700,
        fontSize: '14px',
        color: 'rgba(9, 12, 16, 0.6)'
    },
    sample: {
        fontWeight: 400,
        fontStyle: 'italic',
        fontSize: '14px',
        color: 'rgba(9, 12, 16, 0.6)'
    },
    playStopIcon: {
        color: 'black',
        width: '24px;',
        height: '24px'
    },
    speakerCard: {
        width: '200px',
        height: '88px',
        borderRadius: '4px',
        padding: '4px',
        marginBottom: '10px',
        marginRight: `0 !important`,
        border: 'solid rgba(9, 12, 16, 0.08) 1px',
        cursor: 'pointer'
    },
    container: {
        maxHeight: '400px',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
            width: '20px'
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#D8D8D8',
            borderRadius: '20px',
            border: '6px solid transparent',
            backgroundClip: 'content-box'
        }
    },
    speakerSelected: {
        border: 'solid #CB0000 1px',
        backgroundColor: '#FAE5E5'
    },
    icon: {
        marginRight: '4px !important',
        color: 'white',
        backgroundColor: '#F5F5F5'
    }
})

type Props = {
    languageCode: string
    currentSpeaker: SpeakerType | null
    onSelect: (_value: SpeakerType) => void
}

export const SpeakerField = memo(({ currentSpeaker, languageCode, onSelect }: Props) => {
    const dispatch = useDispatch()

    const { playbookId } = useParams<{ playbookId: string }>()

    const { activeStep } = useSelector(state => state.qgEditor.present)

    const {
        audioConfigs,
        userPreferences: { speakerId }
    } = useSelector(state => state.configs)

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    // used for toggling the play pause icon for a specific speaker
    const [playSpeaker, setPlaySpeaker] = useState<SpeakerType | null>(null)
    // current playing time
    const [currentTime, setCurrentTime] = useState('00:00')
    // used to maintain the current playing state
    const [playStatus, setPlayStatus] = useState<playMode>(playMode.STOP)

    const audioRef = useRef<HTMLMediaElement | null>(null)

    const options = useMemo(() => {
        return audioConfigs.speakers.filter(
            (config: { langCode: string }) => config.langCode === languageCode
        )
    }, [audioConfigs.speakers, languageCode])

    // use the amount of speaker to set the dialog width (with or without scroll bar)
    const classes = useStyles({ length: options.length })

    const openMenu = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)

    const closeMenu = () => {
        setAnchorEl(null)
        setPlaySpeaker(null)
        setPlayStatus(playMode.STOP)
    }

    const initialSpeaker = useMemo(() => {
        if (!options.length) return null
        if (!speakerId) return options?.[0]

        const initialOption = options.find(option => option.id === speakerId)
        if (initialOption) return initialOption

        return options[0]
    }, [options, speakerId])

    useEffect(() => {
        if (currentSpeaker && languageCode && currentSpeaker.langCode === languageCode) return

        if (initialSpeaker) onSelect(initialSpeaker)
    }, [initialSpeaker, languageCode, onSelect, currentSpeaker])

    return (
        <>
            <audio
                src={playSpeaker?.sampleAudioUrl ?? ''}
                ref={audioRef}
                onPlay={() => {
                    setPlayStatus(playMode.PLAY)
                    logToAnalytics('voiceover_play_speaker_preview', {
                        playbookId,
                        activeStep,
                        speaker: playSpeaker
                    })
                }}
                onPause={() => setPlayStatus(playMode.STOP)}
                onTimeUpdate={(event: any) => {
                    setCurrentTime(getTimeString(Number(event?.target?.currentTime)).toString())
                }}
                onEnded={() => {
                    setPlayStatus(playMode.STOP)
                    setCurrentTime('00:00')
                }}
            />

            {/* selected speaker image and name + selection arrow */}
            <Box
                style={{ width: '154px', height: '45px', cursor: 'pointer' }}
                pt={0}
                onClick={openMenu}
            >
                <SpacedGroup
                    alignContent="center"
                    data-cy="editor-speaker"
                    data-intercom="editor-speaker"
                >
                    <img src={currentSpeaker?.avatarUrl} width="40" height="40" alt="avatar" />
                    <SpacedGroup flexDirection="column" spacing={0.25}>
                        <SpacedGroup>
                            <Box style={{ fontSize: '14px', fontWeight: 500 }}>Speaker</Box>
                            <ArrowDropDownIcon style={{ color: '#090C10' }} />
                        </SpacedGroup>
                        <Box style={{ fontSize: '14px', fontWeight: 400 }}>
                            {currentSpeaker?.name}
                        </Box>
                    </SpacedGroup>
                </SpacedGroup>
            </Box>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                disablePortal
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left'
                }}
                PaperProps={{
                    style: {
                        padding: '16px 16px 4px 16px'
                    }
                }}
            >
                <Paper elevation={0}>
                    <ClickAwayListener onClickAway={closeMenu}>
                        <SpacedGroup
                            data-cy="card-container"
                            flexWrap="wrap"
                            justifyContent="space-between"
                            alignContent="center"
                            className={classes.container}
                            style={{ width: `${options.length <= 10 ? '410px' : '430px'}` }}
                        >
                            {options.map(speaker => {
                                const isPlaying =
                                    playStatus === playMode.PLAY && speaker.id === playSpeaker?.id

                                return (
                                    <SpacedGroup
                                        data-cy="speaker-card"
                                        onClick={() => {
                                            localStorage.setItem('speakerId', speaker.id)
                                            dispatch(setUserPreferences())

                                            onSelect(speaker)
                                            closeMenu()
                                        }}
                                        key={speaker.model}
                                        mr={0}
                                        className={clsx(classes.speakerCard, {
                                            [classes.speakerSelected]:
                                                speaker.id === currentSpeaker?.id
                                        })}
                                    >
                                        <img src={speaker.avatarUrl} width="80" height="80" />
                                        <SpacedGroup flexDirection="column">
                                            <Box className={classes.name}>{speaker.name}</Box>
                                            <SpacedGroup>
                                                <IconButton
                                                    className={classes.icon}
                                                    onClick={event => {
                                                        event.stopPropagation()
                                                        if (playSpeaker?.id !== speaker.id) {
                                                            setPlaySpeaker(speaker)
                                                        }

                                                        delay(0).then(() => {
                                                            if (isPlaying) {
                                                                audioRef.current?.pause()
                                                            } else {
                                                                audioRef.current?.play()
                                                            }
                                                        })
                                                    }}
                                                    size="small"
                                                >
                                                    {isPlaying ? (
                                                        <PauseIcon
                                                            className={classes.playStopIcon}
                                                            fontSize="medium"
                                                        />
                                                    ) : (
                                                        <PlayArrowIcon
                                                            className={classes.playStopIcon}
                                                            fontSize="medium"
                                                        />
                                                    )}
                                                </IconButton>

                                                {playStatus !== playMode.STOP &&
                                                speaker.id === playSpeaker?.id ? (
                                                    <Box className={classes.sample}>
                                                        {currentTime}
                                                    </Box>
                                                ) : (
                                                    <Box className={classes.sample}>Sample</Box>
                                                )}
                                            </SpacedGroup>
                                        </SpacedGroup>
                                    </SpacedGroup>
                                )
                            })}
                        </SpacedGroup>
                    </ClickAwayListener>
                </Paper>
            </Popover>
        </>
    )
})
